/**
 * La tienda en chico, para armar el home desde `/tienda` (migracion 0093).
 *
 * Viven aca y no en `publicacion/` porque son piezas de la TIENDA: negro sobre
 * blanco, con la escala y los pesos de la marca (`design-system/tora/pages/
 * catalogo.md`), no los del sistema. La pantalla de configuracion las pone
 * adentro de sus tarjetas; los botones y los titulos de afuera siguen siendo
 * del sistema.
 */
import { type ReactNode } from "react";
import { formatear } from "@tora/shared";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Check, GripVertical, Pencil, Plus, X } from "lucide-react";

import { cn } from "@/lib/utils";

import type { TarjetaDelHome } from "./home";
import type { OfertaPublica, ProductoPublico } from "./logica";
import { Foto, TarjetaOferta } from "./piezas";

/** El recuadro blanco: lo de adentro se ve como en la tienda, sin importar el tema. */
export function Vidriera({
  apagada,
  sinMargen,
  className,
  children,
}: {
  /** Nada de esta seccion va al home: el fondo lo dice antes que el texto. */
  apagada?: boolean;
  sinMargen?: boolean;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className={cn(
        "rounded-md border border-[#E0E0E0] text-[#0A0A0A]",
        apagada ? "bg-[#F7F7F7]" : "bg-white",
        !sinMargen && "p-4 md:p-5",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Una oferta tal como la ve el cliente en el carrusel del home. Tocarla la
 * edita. Los controles (se ve, orden, duplicar) estan en la lista de abajo:
 * aca va solo lo que el cliente ve, para juzgarlo de un vistazo.
 */
export function OfertaEnVidriera({
  oferta,
  ancha = false,
  producto = null,
  onEditar,
}: {
  oferta: OfertaPublica;
  /** El equipo de la oferta, para su foto. */
  producto?: ProductoPublico | null;
  /** Sola en la fila, ancha como en la tienda. */
  ancha?: boolean;
  onEditar: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onEditar}
      aria-label={`Editar la oferta ${oferta.titulo}`}
      className={cn(
        "flex shrink-0 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A] focus-visible:ring-offset-2",
        ancha && "w-full",
      )}
    >
      {/* Sin WhatsApp la tarjeta se dibuja como texto, no como link: aca
          tocarla edita, no escribe. */}
      <div className={cn("pointer-events-none h-full [&>*]:h-full", ancha && "w-full")}>
        <TarjetaOferta
          oferta={oferta}
          whatsapp={null}
          ancha={ancha}
          producto={producto}
          vistaPrevia
        />
      </div>
    </button>
  );
}

/**
 * Una tarjeta de la tienda que se prende y se apaga.
 *
 * En el home: normal, con un tilde negro. Fuera: gris y con un "+". El estado
 * se lee de lejos por la foto, sin tener que leer el tilde. El lapiz de abajo
 * le cambia el nombre en la tienda.
 */
export function TarjetaElegible({
  tarjeta: t,
  nombre,
  onCambiar,
  onRenombrar,
}: {
  tarjeta: TarjetaDelHome;
  /** El nombre en la tienda: el cargado o el automatico. */
  nombre: string;
  onCambiar: () => void;
  onRenombrar: () => void;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col">
      <button
        type="button"
        aria-pressed={t.enHome}
        aria-label={`${nombre}: ${t.enHome ? "está en el home, tocá para sacarla" : "no está en el home, tocá para ponerla"}`}
        onClick={onCambiar}
        className="group relative rounded-md p-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A]"
      >
        <div
          className={cn(
            "aspect-square w-full overflow-hidden rounded-md transition",
            t.enHome ? "ring-2 ring-[#0A0A0A]" : "opacity-40 grayscale group-hover:opacity-70",
          )}
        >
          <Foto producto={t.principal} chico className="size-full" />
        </div>

        <span
          aria-hidden="true"
          className={cn(
            "absolute right-2 top-2 flex size-6 items-center justify-center rounded-full",
            t.enHome
              ? "bg-[#0A0A0A] text-white"
              : "border border-[#D0D0D0] bg-white text-[#5C5C5C] group-hover:border-[#0A0A0A] group-hover:text-[#0A0A0A]",
          )}
        >
          {t.enHome ? <Check className="size-3.5" /> : <Plus className="size-3.5" />}
        </span>
      </button>

      <NombreConLapiz
        nombre={nombre}
        precio={formatear(t.desde, "ARS")}
        apagado={!t.enHome}
        onRenombrar={onRenombrar}
      />
    </div>
  );
}

/** El nombre y el precio de una tarjeta, con el lapiz para renombrarla. */
function NombreConLapiz({
  nombre,
  precio,
  apagado,
  onRenombrar,
}: {
  nombre: string;
  precio: string;
  apagado?: boolean;
  onRenombrar: () => void;
}): JSX.Element {
  return (
    <div className="flex items-start gap-1 px-1 pt-1.5">
      <div className="min-w-0 flex-1">
        <span
          className={cn(
            "line-clamp-2 text-xs font-medium leading-tight",
            apagado && "text-[#8A8A8A]",
          )}
        >
          {nombre}
        </span>
        <span className="text-[11px] tabular-nums text-[#6E6E6E]">{precio}</span>
      </div>
      <button
        type="button"
        onClick={onRenombrar}
        aria-label={`Cambiar el nombre de ${nombre} en la tienda`}
        className="-mr-1 -mt-1 flex size-7 shrink-0 items-center justify-center rounded-md text-[#8A8A8A] hover:bg-[#F2F2F2] hover:text-[#0A0A0A]"
      >
        <Pencil className="size-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * Un destacado: se arrastra para cambiarlo de lugar, la cruz lo saca de los
 * destacados (sigue publicado) y el lapiz lo renombra. Se arrastra igual que
 * una oferta: el mouse desde toda la tarjeta, el dedo dejandola apretada, el
 * teclado desde el asa.
 */
export function TarjetaDestacada({
  tarjeta: t,
  nombre,
  lugar,
  onQuitar,
  onRenombrar,
}: {
  tarjeta: TarjetaDelHome;
  nombre: string;
  /** 1 es el primero: el numero ayuda a leer el orden de un vistazo. */
  lugar: number;
  onQuitar: () => void;
  onRenombrar: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: t.clave,
  });
  const { onKeyDown, ...conPuntero } = listeners ?? {};

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex min-w-0 touch-manipulation flex-col",
        isDragging && "relative z-10 opacity-80",
      )}
    >
      <div className="relative cursor-grab rounded-md p-1 active:cursor-grabbing" {...conPuntero}>
        <div className="aspect-square w-full overflow-hidden rounded-md ring-1 ring-[#E0E0E0]">
          <Foto producto={t.principal} chico className="size-full" />
        </div>
        <span className="absolute left-2 top-2 flex h-6 min-w-6 items-center justify-center rounded-full bg-[#0A0A0A] px-1.5 text-[11px] font-semibold tabular-nums text-white">
          {lugar}
        </span>
        <button
          type="button"
          onClick={onQuitar}
          aria-label={`Sacar ${nombre} de los destacados`}
          className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full border border-[#D0D0D0] bg-white text-[#5C5C5C] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
        >
          <X className="size-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={`Mover ${nombre}`}
          className="absolute bottom-2 right-2 flex size-7 items-center justify-center rounded-md bg-white/90 text-[#6E6E6E]"
          {...attributes}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLButtonElement> | undefined}
        >
          <GripVertical className="size-4" aria-hidden="true" />
        </button>
      </div>

      <NombreConLapiz
        nombre={nombre}
        precio={formatear(t.desde, "ARS")}
        onRenombrar={onRenombrar}
      />
    </div>
  );
}

/** "Ver las 48" / "Ver menos", al pie de una seccion larga. */
export function BotonVerMas({
  abierta,
  total,
  onClick,
}: {
  abierta: boolean;
  total: number;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-3 w-full rounded-md py-2 text-sm font-semibold text-[#0A0A0A] hover:bg-[#F2F2F2]"
    >
      {abierta ? "Ver menos" : `Ver las ${total}`}
    </button>
  );
}
